import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLang } from "../contexts/LanguageContext";
import { formatApiError } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { QrCode, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const tokenMissing = !token || token.length < 20;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error(t("auth.passwordTooShort"));
      return;
    }
    if (pw !== pw2) {
      toast.error(t("auth.passwordsDontMatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-white via-purple-50/40 to-white text-zinc-900 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-purple-950/30 dark:to-zinc-950 dark:text-zinc-100 relative overflow-hidden"
      data-testid="reset-password-page"
      style={{ marginTop: "-4rem", paddingTop: "4rem" }}
    >
      <div className="absolute -top-40 left-1/4 w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-fuchsia-600/10 blur-[120px] pointer-events-none" />

      <div className="grid place-items-center min-h-[calc(100vh-4rem)] p-8 relative">
        <div className="w-full max-w-sm">
          <div className="w-12 h-12 rounded-2xl bg-purple-600 grid place-items-center text-white shadow-lg shadow-purple-600/40 mb-5">
            {done ? <CheckCircle2 className="h-6 w-6" /> : tokenMissing ? <AlertCircle className="h-6 w-6" /> : <QrCode className="h-6 w-6" />}
          </div>

          {done ? (
            <div className="space-y-5" data-testid="reset-password-success">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{t("auth.resetSuccessTitle")}</h1>
                <p className="text-zinc-500 dark:text-zinc-400 mt-2 text-sm leading-relaxed">
                  {t("auth.resetSuccessBody")}
                </p>
              </div>
              <Link
                to="/login"
                className="inline-block w-full text-center rounded-2xl h-11 leading-[2.75rem] bg-purple-600 hover:bg-purple-700 text-white font-medium shadow-lg shadow-purple-600/30"
                data-testid="reset-password-go-login"
              >
                {t("auth.goToLogin")}
              </Link>
            </div>
          ) : tokenMissing ? (
            <div className="space-y-5" data-testid="reset-password-missing-token">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{t("auth.resetTitle")}</h1>
                <p className="text-zinc-500 dark:text-zinc-400 mt-2 text-sm leading-relaxed">
                  {t("auth.missingToken")}
                </p>
              </div>
              <Link
                to="/forgot-password"
                className="inline-block w-full text-center rounded-2xl h-11 leading-[2.75rem] bg-purple-600 hover:bg-purple-700 text-white font-medium shadow-lg shadow-purple-600/30"
                data-testid="reset-password-request-new"
              >
                {t("auth.sendResetLink")}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{t("auth.resetTitle")}</h1>
                <p className="text-zinc-500 dark:text-zinc-400 mt-2 text-sm leading-relaxed">
                  {t("auth.resetSubtitle")}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw" className="text-zinc-700 dark:text-zinc-300">
                  {t("auth.newPassword")}
                </Label>
                <Input
                  id="pw"
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="reset-password-new"
                  className="rounded-xl h-11"
                  autoComplete="new-password"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw2" className="text-zinc-700 dark:text-zinc-300">
                  {t("auth.confirmPassword")}
                </Label>
                <Input
                  id="pw2"
                  type="password"
                  required
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-password-confirm"
                  className="rounded-xl h-11"
                  autoComplete="new-password"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full rounded-2xl h-11 bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/30"
                data-testid="reset-password-submit"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("auth.resetCta")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
